import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  PARAM_VALUE_MAX_LENGTH,
  isAnalyticsEnabled,
  setPageContext,
  trackEvent,
  trackPageView,
} from "@/analytics";

const IGNORE_ATTRIBUTE = "data-analytics-ignore";
const LABEL_ATTRIBUTE = "data-analytics-label";

// Consumers mounted outside the provider get no-ops instead of a crash, the way
// ImportTargetContext seeds a full default value.
const DISABLED_ANALYTICS = {
  isEnabled: false,
  trackEvent: () => {},
  trackButtonClick: () => {},
  trackSelectContent: () => {},
};

const AnalyticsContext = createContext(DISABLED_ANALYTICS);

export function useAnalytics() {
  return useContext(AnalyticsContext);
}

function sanitizeLabel(value) {
  const collapsed = String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!collapsed) return null;
  return collapsed.slice(0, PARAM_VALUE_MAX_LENGTH);
}

function resolveLabel(labelOrEvent) {
  if (typeof labelOrEvent === "string") return sanitizeLabel(labelOrEvent);

  const element = labelOrEvent?.currentTarget ?? labelOrEvent;
  if (!(element instanceof Element)) return null;
  if (element.closest(`[${IGNORE_ATTRIBUTE}]`)) return null;

  const labelled = element.closest(`[${LABEL_ATTRIBUTE}]`);
  return sanitizeLabel(
    labelled?.getAttribute(LABEL_ATTRIBUTE) ||
      element.getAttribute("aria-label") ||
      element.textContent
  );
}

// Record ids in the path (/contacts/5) are masked so page_title is a stable,
// non-identifying route pattern, not a per-record value. document.title is a
// record name on detail screens and the record query updates it only after
// this page view has been sent.
function toRoutePattern(pathname) {
  return pathname.replace(/\/\d+(?=\/|$)/g, "/:id");
}

// GA4 rejects a content_id made only of digits, so a bare record id is
// qualified with the type it came from.
function toContentId(contentType, contentId) {
  const value = String(contentId ?? "").trim();
  if (!value) return null;
  if (!/^\d+$/.test(value)) return value;
  return `${contentType || "id"}_${value}`;
}

export function AnalyticsProvider({ children }) {
  const { pathname } = useLocation();
  const lastPageViewRef = useRef(null);

  useEffect(() => {
    // StrictMode replays effects in development, and the same screen twice is a
    // duplicate page view in any case.
    if (lastPageViewRef.current?.pathname === pathname) return;

    // SECURITY: send only the id-masked route pattern. The raw path carries
    // record ids — which both identify the record and split every record into
    // its own row — and the query string carries the password-setup token and
    // table search/filter state. Google's guidance is that the URL be free of
    // anything it could recognise as PII.
    const routePattern = toRoutePattern(pathname);
    const pageLocation = window.location.origin + routePattern;
    // document.referrer keeps pointing at whatever sent the user to the app, so
    // an in-app navigation has to supply the previous screen itself.
    const pageReferrer = lastPageViewRef.current?.url || document.referrer;
    lastPageViewRef.current = { pathname, url: pageLocation };

    // Set the context rather than passing it per event: gtag would otherwise
    // attach the document's own URL and title to button_click and
    // select_content, reintroducing the ids, query string and record name.
    setPageContext({
      page_location: pageLocation,
      page_title: routePattern,
      ...(pageReferrer && { page_referrer: pageReferrer }),
    });
    trackPageView();
  }, [pathname]);

  const trackButtonClick = useCallback((labelOrEvent, params = {}) => {
    const label = resolveLabel(labelOrEvent);
    if (!label) return;
    trackEvent("button_click", { label, ...params });
  }, []);

  const trackSelectContent = useCallback(
    ({ contentType, contentId, ...params }) => {
      const id = toContentId(contentType, contentId);
      trackEvent("select_content", {
        ...(contentType && { content_type: contentType }),
        ...(id && { content_id: id }),
        ...params,
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      isEnabled: isAnalyticsEnabled(),
      trackEvent,
      trackButtonClick,
      trackSelectContent,
    }),
    [trackButtonClick, trackSelectContent]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
